// src/lib/modals.js
// Create/edit modal forms + attachment helpers.

import { state, store } from './store.js';
import { getAttachmentSignedUrl, MAX_ATTACHMENT_SIZE } from './supabase.js';
import {
  escapeHtml, showModal, closeModal, toast, runAsync,
  getCompany, getContact, getDeal, getTicket, getAttachment, contactName,
  daysFromNow, todayStr, fmtFileSize, fmtDate, dealStatus
} from './helpers.js';
import { render, navigate, getCurrentView } from './router.js';

// =============== COMPANY ===============
export function editCompany(id) {
  const co = id ? getCompany(id) : { id: null, name:'', industry:'', segment:'', website:'', phone:'', address:'', notes:'' };
  const body = `
    <div class="field"><label>Company name</label>
      <input type="text" id="f-name" value="${escapeHtml(co.name)}" autofocus /></div>
    <div class="field-row">
      <div class="field"><label>Industry</label>
        <select id="f-industry">
          <option value="">—</option>
          ${state.settings.industries.map(i => `<option value="${escapeHtml(i)}" ${co.industry===i?'selected':''}>${escapeHtml(i)}</option>`).join('')}
        </select>
      </div>
      <div class="field"><label>Segment</label>
        <select id="f-segment">
          <option value="">—</option>
          ${state.settings.segments.map(s => `<option value="${escapeHtml(s)}" ${co.segment===s?'selected':''}>${escapeHtml(s)}</option>`).join('')}
        </select>
      </div>
    </div>
    <div class="field-row">
      <div class="field"><label>Website</label><input type="text" id="f-website" value="${escapeHtml(co.website)}" /></div>
      <div class="field"><label>Phone</label><input type="text" id="f-phone" value="${escapeHtml(co.phone)}" /></div>
    </div>
    <div class="field"><label>Address</label><input type="text" id="f-address" value="${escapeHtml(co.address)}" /></div>
    <div class="field"><label>Notes</label><textarea id="f-notes">${escapeHtml(co.notes)}</textarea></div>
  `;
  const footer = `
    ${id ? `<button class="btn danger" onclick="window._crm.deleteCompany('${id}')">Delete</button>` : '<span></span>'}
    <div class="right">
      <button class="btn ghost" onclick="window._crm.closeModal()">Cancel</button>
      <button class="btn primary" id="save-company-btn" onclick="window._crm.saveCompany('${id || ''}')">${id ? 'Save changes' : 'Create company'}</button>
    </div>
  `;
  showModal({ title: id ? 'Edit company' : 'New company', body, footer });
}
export async function saveCompany(id) {
  const name = document.getElementById('f-name').value.trim();
  if (!name) { toast('Name is required', true); return; }
  const data = {
    name,
    industry: document.getElementById('f-industry').value,
    segment: document.getElementById('f-segment').value,
    website: document.getElementById('f-website').value.trim(),
    phone: document.getElementById('f-phone').value.trim(),
    address: document.getElementById('f-address').value.trim(),
    notes: document.getElementById('f-notes').value.trim()
  };
  const btn = document.getElementById('save-company-btn');
  if (btn) { btn.disabled = true; btn.textContent = 'Saving…'; }
  try {
    if (id) await runAsync(() => store.companies.update(id, data), 'Company updated');
    else await runAsync(() => store.companies.create(data), 'Company created');
    closeModal(); render();
  } catch (_) {
    if (btn) { btn.disabled = false; btn.textContent = id ? 'Save changes' : 'Create company'; }
  }
}
export async function deleteCompany(id) {
  if (!confirm('Delete this company? Contacts and deals will lose their link, attached files will be removed.')) return;
  await runAsync(() => store.companies.remove(id), 'Company deleted');
  closeModal();
  if (getCurrentView() === 'company-detail') navigate('companies'); else render();
}

// =============== CONTACT ===============
export function editContact(id, prefill) {
  prefill = prefill || {};
  const c = id ? getContact(id) : { id: null, firstName:'', lastName:'', title:'', email:'', phone:'', companyId: prefill.companyId||'', lifecycle:'', notes:'' };
  const body = `
    <div class="field-row">
      <div class="field"><label>First name</label><input type="text" id="f-first" value="${escapeHtml(c.firstName)}" autofocus /></div>
      <div class="field"><label>Last name</label><input type="text" id="f-last" value="${escapeHtml(c.lastName)}" /></div>
    </div>
    <div class="field"><label>Title / role</label><input type="text" id="f-title" value="${escapeHtml(c.title)}" /></div>
    <div class="field-row">
      <div class="field"><label>Company</label>
        <select id="f-company">
          <option value="">—</option>
          ${state.companies.map(co => `<option value="${co.id}" ${co.id===c.companyId?'selected':''}>${escapeHtml(co.name)}</option>`).join('')}
        </select>
      </div>
      <div class="field"><label>Lifecycle stage</label>
        <select id="f-lifecycle">
          <option value="">—</option>
          <option value="lead" ${c.lifecycle==='lead'?'selected':''}>Lead</option>
          <option value="opportunity" ${c.lifecycle==='opportunity'?'selected':''}>Opportunity</option>
          <option value="customer" ${c.lifecycle==='customer'?'selected':''}>Customer</option>
          <option value="evangelist" ${c.lifecycle==='evangelist'?'selected':''}>Evangelist</option>
        </select>
      </div>
    </div>
    <div class="field-row">
      <div class="field"><label>Email</label><input type="email" id="f-email" value="${escapeHtml(c.email)}" /></div>
      <div class="field"><label>Phone</label><input type="text" id="f-phone" value="${escapeHtml(c.phone)}" /></div>
    </div>
    <div class="field"><label>Notes</label><textarea id="f-notes">${escapeHtml(c.notes)}</textarea></div>
  `;
  const footer = `
    ${id ? `<button class="btn danger" onclick="window._crm.deleteContact('${id}')">Delete</button>` : '<span></span>'}
    <div class="right">
      <button class="btn ghost" onclick="window._crm.closeModal()">Cancel</button>
      <button class="btn primary" id="save-contact-btn" onclick="window._crm.saveContact('${id || ''}')">${id ? 'Save changes' : 'Create contact'}</button>
    </div>
  `;
  showModal({ title: id ? 'Edit contact' : 'New contact', body, footer });
}
export async function saveContact(id) {
  const first = document.getElementById('f-first').value.trim();
  const last = document.getElementById('f-last').value.trim();
  if (!first && !last) { toast('Name is required', true); return; }
  const data = {
    firstName: first, lastName: last,
    title: document.getElementById('f-title').value.trim(),
    companyId: document.getElementById('f-company').value || null,
    lifecycle: document.getElementById('f-lifecycle').value,
    email: document.getElementById('f-email').value.trim(),
    phone: document.getElementById('f-phone').value.trim(),
    notes: document.getElementById('f-notes').value.trim()
  };
  const btn = document.getElementById('save-contact-btn');
  if (btn) { btn.disabled = true; btn.textContent = 'Saving…'; }
  try {
    if (id) await runAsync(() => store.contacts.update(id, data), 'Contact updated');
    else await runAsync(() => store.contacts.create(data), 'Contact created');
    closeModal(); render();
  } catch (_) {
    if (btn) { btn.disabled = false; btn.textContent = id ? 'Save changes' : 'Create contact'; }
  }
}
export async function deleteContact(id) {
  if (!confirm('Delete this contact?')) return;
  await runAsync(() => store.contacts.remove(id), 'Contact deleted');
  closeModal();
  if (getCurrentView() === 'contact-detail') navigate('contacts'); else render();
}

// =============== DEAL ===============
// Deal form now includes: status, currency, quote number/date, quote document upload.
export function editDeal(id, prefill) {
  prefill = prefill || {};
  const d = id ? getDeal(id) : {
    id:null, title:'', companyId: prefill.companyId||'', contactId: prefill.contactId||'',
    value:'', stage:'lead', status:'active',
    currency: state.settings.currency,
    priority:'medium', expectedClose:daysFromNow(30), notes:'',
    quoteNumber:'', quoteDate:'', quoteAttachmentId:null
  };
  // Find existing quote attachment if any
  const quoteAtt = d.quoteAttachmentId ? getAttachment(d.quoteAttachmentId) : null;

  const body = `
    <div class="field"><label>Deal title</label><input type="text" id="f-title" value="${escapeHtml(d.title)}" autofocus /></div>
    <div class="field-row">
      <div class="field"><label>Company</label>
        <select id="f-company">
          <option value="">—</option>
          ${state.companies.map(co => `<option value="${co.id}" ${co.id===d.companyId?'selected':''}>${escapeHtml(co.name)}</option>`).join('')}
        </select>
      </div>
      <div class="field"><label>Primary contact</label>
        <select id="f-contact">
          <option value="">—</option>
          ${state.contacts.map(ct => `<option value="${ct.id}" ${ct.id===d.contactId?'selected':''}>${escapeHtml(contactName(ct))}</option>`).join('')}
        </select>
      </div>
    </div>

    <div class="field-row-3">
      <div class="field"><label>Currency</label>
        <select id="f-currency">
          ${state.settings.currencies.map(cur => `<option value="${cur}" ${cur===(d.currency||state.settings.currency)?'selected':''}>${cur}</option>`).join('')}
        </select>
      </div>
      <div class="field"><label>Value</label><input type="number" id="f-value" value="${escapeHtml(d.value)}" step="1" /></div>
      <div class="field"><label>Expected close</label><input type="date" id="f-close" value="${escapeHtml(d.expectedClose)}" /></div>
    </div>

    <div class="field-row-3">
      <div class="field"><label>Stage</label>
        <select id="f-stage">
          ${state.settings.dealStages.map(s => `<option value="${s.id}" ${s.id===d.stage?'selected':''}>${escapeHtml(s.name)} (${s.probability}%)</option>`).join('')}
          <option value="lost" ${d.stage==='lost'?'selected':''}>Lost</option>
        </select>
      </div>
      <div class="field"><label>Status</label>
        <select id="f-status">
          ${state.settings.dealStatuses.map(s => `<option value="${s.id}" ${s.id===(d.status||'active')?'selected':''}>${escapeHtml(s.name)}</option>`).join('')}
        </select>
      </div>
      <div class="field"><label>Priority</label>
        <select id="f-priority">
          <option value="">—</option>
          ${state.settings.priorities.map(p => `<option value="${p}" ${d.priority===p?'selected':''}>${p.toUpperCase()}</option>`).join('')}
        </select>
      </div>
    </div>

    <div style="border:1px solid var(--border); background: var(--bg-soft); border-radius: 6px; padding: 14px 16px; margin: 14px 0 10px;">
      <div style="font-family:'JetBrains Mono',monospace; font-size:10px; letter-spacing:0.18em; text-transform:uppercase; color:var(--ink-muted); margin-bottom: 10px;">Quote / Proposal</div>
      <div class="field-row">
        <div class="field"><label>Quote number</label><input type="text" id="f-quote-number" value="${escapeHtml(d.quoteNumber)}" placeholder="e.g. Q-2026-041" /></div>
        <div class="field"><label>Quote date</label><input type="date" id="f-quote-date" value="${escapeHtml(d.quoteDate)}" /></div>
      </div>
      <div class="field" style="margin-bottom: 0;">
        <label>Quote document</label>
        <div id="quote-doc-row">
          ${quoteAtt
            ? `<div style="display:flex; align-items:center; gap:10px; padding: 8px 12px; background: var(--bg-base); border: 1px solid var(--border); border-radius: 4px;">
                 <span style="font-size:18px;">📄</span>
                 <div style="flex:1;">
                   <div style="font-size:13px; color:var(--ink); font-weight:500;">${escapeHtml(quoteAtt.fileName)}</div>
                   <div class="inline-note" style="margin-top:2px;">${escapeHtml(fmtFileSize(quoteAtt.fileSize))}</div>
                 </div>
                 <button type="button" class="btn small" onclick="window._crm.viewAttachment('${quoteAtt.id}')">View</button>
                 <button type="button" class="btn small ghost" onclick="window._crm.detachQuoteDoc()">Replace</button>
               </div>`
            : `<input type="file" id="f-quote-file" accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg" />
               <div class="help">Optional. Upload the quote/proposal PDF (or doc/spreadsheet/image). Max 10 MB. Saved when you save the deal.</div>`
          }
        </div>
      </div>
    </div>

    <div class="field"><label>Notes</label><textarea id="f-notes">${escapeHtml(d.notes)}</textarea></div>
  `;
  const footer = `
    ${id ? `<button class="btn danger" onclick="window._crm.deleteDeal('${id}')">Delete</button>` : '<span></span>'}
    <div class="right">
      <button class="btn ghost" onclick="window._crm.closeModal()">Cancel</button>
      <button class="btn primary" id="save-deal-btn" onclick="window._crm.saveDeal('${id || ''}')">${id ? 'Save changes' : 'Create deal'}</button>
    </div>
  `;
  showModal({ title: id ? 'Edit deal' : 'New deal', body, footer, wide: true });

  // Track in-modal state so "Replace" can swap back to the file picker without re-rendering everything
  window._dealModal = { existingQuoteAttId: d.quoteAttachmentId || null, removeQuoteAtt: false };
}
export function detachQuoteDoc() {
  // Swap the "current quote" display for a file input, allowing a new upload
  const row = document.getElementById('quote-doc-row');
  if (!row) return;
  row.innerHTML = `<input type="file" id="f-quote-file" accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg" />
    <div class="help">Pick a new file to replace the existing quote document.</div>`;
  if (window._dealModal) window._dealModal.removeQuoteAtt = true;
}
export async function saveDeal(id) {
  const title = document.getElementById('f-title').value.trim();
  if (!title) { toast('Title is required', true); return; }
  const data = {
    title,
    companyId: document.getElementById('f-company').value || null,
    contactId: document.getElementById('f-contact').value || null,
    value: document.getElementById('f-value').value,
    currency: document.getElementById('f-currency').value,
    stage: document.getElementById('f-stage').value,
    status: document.getElementById('f-status').value,
    priority: document.getElementById('f-priority').value,
    expectedClose: document.getElementById('f-close').value,
    quoteNumber: document.getElementById('f-quote-number').value.trim(),
    quoteDate: document.getElementById('f-quote-date').value,
    notes: document.getElementById('f-notes').value.trim()
  };
  const fileEl = document.getElementById('f-quote-file');
  const fileToUpload = fileEl && fileEl.files && fileEl.files[0] ? fileEl.files[0] : null;

  // Validate file size early
  if (fileToUpload && fileToUpload.size > MAX_ATTACHMENT_SIZE) {
    toast('File too large. Max 10 MB.', true);
    return;
  }

  const btn = document.getElementById('save-deal-btn');
  if (btn) { btn.disabled = true; btn.textContent = 'Saving…'; }

  try {
    let dealId = id;

    // Step 1: create or update the deal record
    if (id) {
      // Preserve current quote attachment unless the user removed it
      if (window._dealModal && !window._dealModal.removeQuoteAtt) {
        data.quoteAttachmentId = window._dealModal.existingQuoteAttId;
      } else {
        data.quoteAttachmentId = null;
      }
      await runAsync(() => store.deals.update(id, data));
    } else {
      data.quoteAttachmentId = null;
      dealId = await runAsync(() => store.deals.create(data));
    }

    // Step 2: if there's a file to upload, do it now and link it
    if (fileToUpload && dealId) {
      if (btn) btn.textContent = 'Uploading file…';

      // If we're replacing an existing quote, remove the old one first
      if (id && window._dealModal && window._dealModal.removeQuoteAtt && window._dealModal.existingQuoteAttId) {
        try { await store.attachments.remove(window._dealModal.existingQuoteAttId); }
        catch (e) { console.warn('Failed to remove old quote attachment', e); }
      }

      const att = await runAsync(() => store.attachments.upload(fileToUpload, 'deal', dealId));
      await runAsync(() => store.deals.setQuoteAttachment(dealId, att.id));
    } else if (id && window._dealModal && window._dealModal.removeQuoteAtt && window._dealModal.existingQuoteAttId && !fileToUpload) {
      // User clicked Replace but didn't pick a new file → just remove the old one
      try { await store.attachments.remove(window._dealModal.existingQuoteAttId); }
      catch (e) { console.warn('Failed to remove old quote attachment', e); }
      await runAsync(() => store.deals.setQuoteAttachment(dealId, null));
    }

    toast(id ? 'Deal updated' : 'Deal created');
    closeModal();
    delete window._dealModal;
    render();
  } catch (_) {
    if (btn) { btn.disabled = false; btn.textContent = id ? 'Save changes' : 'Create deal'; }
  }
}
export async function deleteDeal(id) {
  if (!confirm('Delete this deal? Attached files will be removed too.')) return;
  await runAsync(() => store.deals.remove(id), 'Deal deleted');
  closeModal();
  if (getCurrentView() === 'deal-detail') navigate('deals'); else render();
}
export async function setDealStage(id, stage) {
  await runAsync(() => store.deals.updateStage(id, stage));
  toast('Stage updated');
  render();
}
export async function setDealStatus(id, status) {
  await runAsync(() => store.deals.updateStatus(id, status));
  const s = dealStatus(status);
  toast('Status: ' + s.name);
  render();
}
export function markDealWon(id) { setDealStage(id, 'won'); }

// =============== TICKET ===============
export function editTicket(id, prefill) {
  prefill = prefill || {};
  const t = id ? getTicket(id) : { id:null, subject:'', description:'', companyId: prefill.companyId||'', contactId: prefill.contactId||'', stage:'new', priority:'medium' };
  const body = `
    <div class="field"><label>Subject</label><input type="text" id="f-subject" value="${escapeHtml(t.subject)}" autofocus /></div>
    <div class="field"><label>Description</label><textarea id="f-desc">${escapeHtml(t.description||'')}</textarea></div>
    <div class="field-row">
      <div class="field"><label>Company</label>
        <select id="f-company">
          <option value="">—</option>
          ${state.companies.map(co => `<option value="${co.id}" ${co.id===t.companyId?'selected':''}>${escapeHtml(co.name)}</option>`).join('')}
        </select>
      </div>
      <div class="field"><label>Contact</label>
        <select id="f-contact">
          <option value="">—</option>
          ${state.contacts.map(c => `<option value="${c.id}" ${c.id===t.contactId?'selected':''}>${escapeHtml(contactName(c))}</option>`).join('')}
        </select>
      </div>
    </div>
    <div class="field-row">
      <div class="field"><label>Stage</label>
        <select id="f-stage">
          ${state.settings.ticketStages.map(s => `<option value="${s.id}" ${s.id===t.stage?'selected':''}>${escapeHtml(s.name)}</option>`).join('')}
        </select>
      </div>
      <div class="field"><label>Priority</label>
        <select id="f-priority">
          ${state.settings.priorities.map(p => `<option value="${p}" ${t.priority===p?'selected':''}>${p.toUpperCase()}</option>`).join('')}
        </select>
      </div>
    </div>
  `;
  const footer = `
    ${id ? `<button class="btn danger" onclick="window._crm.deleteTicket('${id}')">Delete</button>` : '<span></span>'}
    <div class="right">
      <button class="btn ghost" onclick="window._crm.closeModal()">Cancel</button>
      <button class="btn primary" id="save-ticket-btn" onclick="window._crm.saveTicket('${id || ''}')">${id ? 'Save changes' : 'Create ticket'}</button>
    </div>
  `;
  showModal({ title: id ? 'Edit ticket' : 'New ticket', body, footer });
}
export async function saveTicket(id) {
  const subject = document.getElementById('f-subject').value.trim();
  if (!subject) { toast('Subject required', true); return; }
  const data = {
    subject,
    description: document.getElementById('f-desc').value.trim(),
    companyId: document.getElementById('f-company').value || null,
    contactId: document.getElementById('f-contact').value || null,
    stage: document.getElementById('f-stage').value,
    priority: document.getElementById('f-priority').value
  };
  const btn = document.getElementById('save-ticket-btn');
  if (btn) { btn.disabled = true; btn.textContent = 'Saving…'; }
  try {
    if (id) await runAsync(() => store.tickets.update(id, data), 'Ticket updated');
    else await runAsync(() => store.tickets.create(data), 'Ticket created');
    closeModal(); render();
  } catch (_) {
    if (btn) { btn.disabled = false; btn.textContent = id ? 'Save changes' : 'Create ticket'; }
  }
}
export async function deleteTicket(id) {
  if (!confirm('Delete this ticket? Attached files will be removed too.')) return;
  await runAsync(() => store.tickets.remove(id), 'Ticket deleted');
  closeModal();
  if (getCurrentView() === 'ticket-detail') navigate('tickets'); else render();
}
export async function setTicketStage(id, stage) {
  await runAsync(() => store.tickets.updateStage(id, stage));
  toast('Ticket updated');
  render();
}
export function closeTicket(id) { setTicketStage(id, 'closed'); }

// =============== ACTIVITY ===============
export function editActivity(id, prefill) {
  prefill = prefill || {};
  const a = id ? state.activities.find(x => x.id === id) : { id:null, type: prefill.type||'task', subject:'', contactId: prefill.contactId||'', dealId: prefill.dealId||'', ticketId: prefill.ticketId||'', companyId: prefill.companyId||'', date:todayStr(), notes:'', done:false };
  const body = `
    <div class="field-row">
      <div class="field"><label>Type</label>
        <select id="f-type">
          <option value="note" ${a.type==='note'?'selected':''}>Note</option>
          <option value="call" ${a.type==='call'?'selected':''}>Call</option>
          <option value="email" ${a.type==='email'?'selected':''}>Email</option>
          <option value="meeting" ${a.type==='meeting'?'selected':''}>Meeting</option>
          <option value="task" ${a.type==='task'?'selected':''}>Task</option>
        </select>
      </div>
      <div class="field"><label>Date</label><input type="date" id="f-date" value="${escapeHtml(a.date)}" /></div>
    </div>
    <div class="field"><label>Subject</label><input type="text" id="f-subject" value="${escapeHtml(a.subject)}" autofocus /></div>
    <div class="field-row">
      <div class="field"><label>Contact</label>
        <select id="f-contact">
          <option value="">—</option>
          ${state.contacts.map(c => `<option value="${c.id}" ${c.id===a.contactId?'selected':''}>${escapeHtml(contactName(c))}</option>`).join('')}
        </select>
      </div>
      <div class="field"><label>Deal</label>
        <select id="f-deal">
          <option value="">—</option>
          ${state.deals.map(d => `<option value="${d.id}" ${d.id===a.dealId?'selected':''}>${escapeHtml(d.title)}</option>`).join('')}
        </select>
      </div>
    </div>
    <div class="field-row">
      <div class="field"><label>Company</label>
        <select id="f-company">
          <option value="">—</option>
          ${state.companies.map(co => `<option value="${co.id}" ${co.id===a.companyId?'selected':''}>${escapeHtml(co.name)}</option>`).join('')}
        </select>
      </div>
      <div class="field"><label>Ticket</label>
        <select id="f-ticket">
          <option value="">—</option>
          ${state.tickets.map(t => `<option value="${t.id}" ${t.id===a.ticketId?'selected':''}>${escapeHtml(t.subject)}</option>`).join('')}
        </select>
      </div>
    </div>
    <div class="field"><label>Notes</label><textarea id="f-notes">${escapeHtml(a.notes)}</textarea></div>
    <div class="field" style="display:flex; align-items:center; gap:8px;">
      <input type="checkbox" id="f-done" ${a.done?'checked':''} style="width:auto;" />
      <label for="f-done" style="margin:0; cursor:pointer;">Completed</label>
    </div>
  `;
  const footer = `
    ${id ? `<button class="btn danger" onclick="window._crm.deleteActivity('${id}')">Delete</button>` : '<span></span>'}
    <div class="right">
      <button class="btn ghost" onclick="window._crm.closeModal()">Cancel</button>
      <button class="btn primary" id="save-act-btn" onclick="window._crm.saveActivity('${id || ''}')">${id ? 'Save changes' : 'Log activity'}</button>
    </div>
  `;
  showModal({ title: id ? 'Edit activity' : 'Log activity', body, footer });
}
export async function saveActivity(id) {
  const subject = document.getElementById('f-subject').value.trim();
  if (!subject) { toast('Subject is required', true); return; }
  const data = {
    type: document.getElementById('f-type').value,
    subject,
    contactId: document.getElementById('f-contact').value || null,
    dealId: document.getElementById('f-deal').value || null,
    companyId: document.getElementById('f-company').value || null,
    ticketId: document.getElementById('f-ticket').value || null,
    date: document.getElementById('f-date').value,
    notes: document.getElementById('f-notes').value.trim(),
    done: document.getElementById('f-done').checked
  };
  const btn = document.getElementById('save-act-btn');
  if (btn) { btn.disabled = true; btn.textContent = 'Saving…'; }
  try {
    if (id) await runAsync(() => store.activities.update(id, data), 'Activity updated');
    else await runAsync(() => store.activities.create(data), 'Activity logged');
    closeModal(); render();
  } catch (_) {
    if (btn) { btn.disabled = false; btn.textContent = id ? 'Save changes' : 'Log activity'; }
  }
}
export async function deleteActivity(id) {
  if (!confirm('Delete this activity?')) return;
  await runAsync(() => store.activities.remove(id), 'Activity deleted');
  closeModal(); render();
}
export async function deleteActivityConfirm(id) {
  if (!confirm('Delete this activity?')) return;
  await runAsync(() => store.activities.remove(id), 'Deleted');
  render();
}
export async function toggleActivity(id) {
  await runAsync(() => store.activities.toggleDone(id));
  render();
}

// =============== ATTACHMENTS ===============
// Renders the "Files" panel for a given parent (company / deal / ticket).
export function renderAttachmentsPanel(parentKind, parentId) {
  const filter = (a) => {
    if (parentKind === 'company') return a.companyId === parentId;
    if (parentKind === 'deal')    return a.dealId    === parentId;
    if (parentKind === 'ticket')  return a.ticketId  === parentId;
    return false;
  };
  const files = state.attachments.filter(filter).sort((a,b) => (b.createdAt||0) - (a.createdAt||0));
  return `<div class="rel-panel">
    <h4>Files (${files.length}) <a onclick="window._crm.uploadAttachmentClick('${parentKind}', '${parentId}')">+ Upload</a></h4>
    <input type="file" id="att-file-${parentKind}-${parentId}" style="display:none" accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.txt" />
    ${files.length
      ? files.map(f => `
        <div class="rel-item" style="display:flex; align-items:center; gap:10px;">
          <span style="font-size:18px;">${attachmentIcon(f.mimeType, f.fileName)}</span>
          <div style="flex:1; min-width: 0;" onclick="window._crm.viewAttachment('${f.id}')">
            <div class="t" style="overflow:hidden; text-overflow:ellipsis; white-space:nowrap;">${escapeHtml(f.fileName)}</div>
            <div class="s">${escapeHtml(fmtFileSize(f.fileSize))} · ${escapeHtml(fmtDate(new Date(f.createdAt).toISOString()))}</div>
          </div>
          <button class="btn small ghost" onclick="event.stopPropagation(); window._crm.downloadAttachment('${f.id}')" title="Download">⬇</button>
          <button class="btn small ghost" onclick="event.stopPropagation(); window._crm.deleteAttachmentConfirm('${f.id}')" title="Delete" style="color: var(--bad);">×</button>
        </div>`).join('')
      : '<div class="inline-note">No files attached.</div>'}
  </div>`;
}
function attachmentIcon(mime, fileName) {
  const ext = (fileName || '').split('.').pop().toLowerCase();
  if (mime?.startsWith('image/')) return '🖼️';
  if (mime === 'application/pdf' || ext === 'pdf') return '📄';
  if (['xls','xlsx','csv'].includes(ext)) return '📊';
  if (['doc','docx'].includes(ext)) return '📝';
  return '📎';
}
export function uploadAttachmentClick(parentKind, parentId) {
  const input = document.getElementById(`att-file-${parentKind}-${parentId}`);
  if (!input) return;
  input.value = '';
  input.onchange = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    if (file.size > MAX_ATTACHMENT_SIZE) { toast('File too large. Max 10 MB.', true); return; }
    toast('Uploading ' + file.name + '…');
    try {
      await runAsync(() => store.attachments.upload(file, parentKind, parentId), 'Uploaded');
      render();
    } catch (_) { /* toast already shown */ }
  };
  input.click();
}
export async function viewAttachment(id) {
  const a = getAttachment(id);
  if (!a) return;
  try {
    const url = await getAttachmentSignedUrl(a.storagePath);
    window.open(url, '_blank');
  } catch (e) {
    toast('Could not open file: ' + e.message, true);
  }
}
export async function downloadAttachment(id) {
  const a = getAttachment(id);
  if (!a) return;
  try {
    const url = await getAttachmentSignedUrl(a.storagePath, { download: true });
    const link = document.createElement('a');
    link.href = url;
    link.download = a.fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch (e) {
    toast('Download failed: ' + e.message, true);
  }
}
export async function deleteAttachmentConfirm(id) {
  const a = getAttachment(id);
  if (!a) return;
  if (!confirm(`Delete "${a.fileName}"? This cannot be undone.`)) return;
  await runAsync(() => store.attachments.remove(id), 'File deleted');
  render();
}

// =============== QUICK NOTE ===============
let _quickNoteType = 'note';
export function renderQuickNote(ctx) {
  const types = [
    { id: 'note', label: 'Note' },
    { id: 'call', label: 'Call' },
    { id: 'email', label: 'Email' },
    { id: 'meeting', label: 'Meeting' },
    { id: 'task', label: 'Task' }
  ];
  _quickNoteType = 'note';
  return `<div class="qnote" id="qnote" data-ctx='${escapeHtml(JSON.stringify(ctx))}'>
    <div class="qnote-tabs">
      ${types.map(t => `<button class="qnote-tab ${t.id==='note'?'active':''}" data-type="${t.id}">${t.label}</button>`).join('')}
    </div>
    <textarea id="qnote-text" placeholder="Add a note, or switch tabs to log a different activity…"></textarea>
    <div class="qrow">
      <div class="qmeta" id="qnote-meta"></div>
      <div style="display:flex; gap:8px;">
        <button class="btn small" onclick="window._crm.clearQuickNote()">Clear</button>
        <button class="btn primary small" id="qnote-save" onclick="window._crm.saveQuickNote()">Save</button>
      </div>
    </div>
  </div>`;
}
export function setupQuickNote() {
  document.querySelectorAll('.qnote-tab').forEach(tab => {
    tab.addEventListener('click', () => {
      document.querySelectorAll('.qnote-tab').forEach(t => t.classList.remove('active'));
      tab.classList.add('active');
      _quickNoteType = tab.dataset.type;
      const meta = document.getElementById('qnote-meta');
      if (_quickNoteType === 'task') {
        meta.innerHTML = '<label style="font-size:10px; color:var(--ink-muted); font-family:\'JetBrains Mono\',monospace;">Due:</label> <input type="date" id="qnote-date" value="' + daysFromNow(1) + '" />';
      } else if (_quickNoteType === 'meeting') {
        meta.innerHTML = '<label style="font-size:10px; color:var(--ink-muted); font-family:\'JetBrains Mono\',monospace;">When:</label> <input type="date" id="qnote-date" value="' + todayStr() + '" />';
      } else {
        meta.innerHTML = '';
      }
      const ta = document.getElementById('qnote-text');
      if (ta) ta.placeholder = `Add a ${_quickNoteType}…`;
    });
  });
}
export function clearQuickNote() {
  const ta = document.getElementById('qnote-text');
  if (ta) ta.value = '';
}
export async function saveQuickNote() {
  const qnote = document.getElementById('qnote');
  if (!qnote) return;
  const ctx = JSON.parse(qnote.dataset.ctx || '{}');
  const text = (document.getElementById('qnote-text').value || '').trim();
  if (!text && _quickNoteType !== 'task') { toast('Enter some content first', true); return; }
  const dateEl = document.getElementById('qnote-date');
  const date = dateEl ? dateEl.value : todayStr();
  const subject = (text.split('\n')[0] || '').slice(0, 80) || _quickNoteType.charAt(0).toUpperCase() + _quickNoteType.slice(1);
  const data = {
    type: _quickNoteType,
    subject, notes: text, date,
    done: _quickNoteType === 'task' ? false : true,
    contactId: ctx.contactId || null,
    dealId: ctx.dealId || null,
    ticketId: ctx.ticketId || null,
    companyId: ctx.companyId || null
  };
  const btn = document.getElementById('qnote-save');
  if (btn) { btn.disabled = true; btn.textContent = 'Saving…'; }
  try {
    await runAsync(() => store.activities.create(data), 'Saved');
    render();
  } catch (_) {
    if (btn) { btn.disabled = false; btn.textContent = 'Save'; }
  }
}

// =============== TIMELINE ===============
export function renderTimeline(activities, opts) {
  const title = (opts && opts.title) || 'Activity';
  const types = ['all', 'note', 'call', 'email', 'meeting', 'task'];
  const sorted = [...activities].sort((a,b) => (b.createdAt||0) - (a.createdAt||0));
  return `<div class="timeline" id="timeline">
    <h3 style="font-family:'Outfit',sans-serif; font-size: 16px; font-weight:500; margin:0 0 14px; color:var(--ink);">${escapeHtml(title)} · ${sorted.length}</h3>
    <div class="timeline-filter">
      ${types.map(t => `<button class="${t==='all'?'active':''}" data-filter="${t}">${t === 'all' ? 'All' : t}</button>`).join('')}
    </div>
    <div class="timeline-events" id="timeline-events">
      ${sorted.length ? sorted.map(renderTimelineEvent).join('') : '<div class="empty">No activity yet.</div>'}
    </div>
  </div>`;
}
function renderTimelineEvent(a) {
  return `<div class="t-event ${escapeHtml(a.type)}" data-type="${escapeHtml(a.type)}">
    <div class="t-event-head">
      <div class="t-event-title">${escapeHtml(a.subject || '(untitled)')} <span class="chip" style="margin-left:6px;">${escapeHtml(a.type)}</span>${a.done ? '' : ' <span class="chip warn">open</span>'}</div>
      <div class="t-event-time">${escapeHtml(new Date(a.createdAt).toLocaleDateString(undefined,{day:'2-digit',month:'short'}) + ' · ' + new Date(a.createdAt).toLocaleTimeString(undefined,{hour:'2-digit',minute:'2-digit'}))}</div>
    </div>
    ${a.notes ? '<div class="t-event-body">' + escapeHtml(a.notes) + '</div>' : ''}
    <div class="t-event-actions">
      <button onclick="window._crm.editActivity('${a.id}')">Edit</button>
      <button onclick="window._crm.deleteActivityConfirm('${a.id}')">Delete</button>
      ${a.type === 'task' ? `<button onclick="window._crm.toggleActivity('${a.id}')">${a.done ? 'Reopen' : 'Mark done'}</button>` : ''}
    </div>
  </div>`;
}
export function setupTimelineFilters() {
  const tl = document.getElementById('timeline');
  if (!tl) return;
  tl.querySelectorAll('.timeline-filter button').forEach(b => {
    b.addEventListener('click', () => {
      tl.querySelectorAll('.timeline-filter button').forEach(x => x.classList.remove('active'));
      b.classList.add('active');
      const f = b.dataset.filter;
      tl.querySelectorAll('.t-event').forEach(ev => {
        const show = f === 'all' || ev.dataset.type === f;
        ev.style.display = show ? '' : 'none';
      });
    });
  });
}
